import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { KeyboardArrowRight, KeyboardArrowLeft, Close } from '@material-ui/icons';
import { isMobileSafari } from 'react-device-detect';
import classnames from 'classnames';
import CollectionsIcon from '@material-ui/icons/Collections';

import callApi from '../../../utils/callApi';
import {
  ANCHOR_PRIVATE_GALLERY,
  ANCHOR_VIEW_PURCHASED_CLIPSTORE_GALLERY,
} from '../../../constants/apiUrls/apiUrls';
import defaultGalleryImage from '../../../assets/default-gallery.png';
import NotificationStore from '../../../store/NotificationStore';
import PayPerViewStore from '../../../store/PayPerViewStore';
import './PrivateGallery.css';
import {
  anchorIsNakedNewsLive,
  buildAnchorCheckoutLink,
  buildAnchorLink,
} from '../../../utils/helpers';

import {
  linkToPaymentPage,
  LockIconContainer,
  PriceForClipStoreItem,
} from './PrivateContentComponents/PrivateContentUtils';
import PrivateContentDescription from './PrivateContentComponents/PrivateContentDescription';
import LikesAndViewsCount from './LikesAndViewsCount';
import SubscribeToViewCTAButton from './SubscribeToViewCTAButton';
import LiveEventsContent from './PrivateContentComponents/LiveEventsContent';


const PrivateGallery = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [currentBodyScrollTop, setCurrentBodyScrollTop] = useState(0);
  const [anchorShortUrl, setAnchorShortUrl] = useState('');
  const [shouldBlurContent, setShouldBlurContent] = useState(true);
  const [userCanAccessContent, setUserCanAccessContent] = useState(false);
  const [isBanned, setIsBanned] = useState(false);

  const {
    media,
    blurContent,
    subscribed,
    anchorId,
    title,
    imageCount,
    coverPhoto,
    showDescription = true,
    liveEvents = false,
    typeOfListing,
    price,
    payPerViewNav,
    customPriceForFanPlus,
    anchors,
    isClipStorePage,
    isPurchasedTab,
    user,
    allAnchors,
  } = props;
  const isFanzone = typeOfListing === 'fanzone';
  const image = coverPhoto || defaultGalleryImage;
  const isClipStoreContent = useMemo(() => typeOfListing === 'clipstore', [typeOfListing]);

  const setBlurringForContent = useCallback((currentAnchor) => {
    const id = currentAnchor?.id || anchorId;
    if ((/* (anchorIsNakedNewsLive(id) || typeOfListing === 'fanzone' ||*/ isPurchasedTab) || !blurContent) {
      setShouldBlurContent(false);
    }
  }, [anchorId, isPurchasedTab, blurContent]);

  const setBanStatus = useCallback((anchor) => {
    const { anchorSubscriptions } = user?.user?.account || {};
    const anchorID = anchor?.id || anchorId;
    if (anchorSubscriptions) {
      const subscribedToAnchor = anchorSubscriptions.filter((anchorData) => parseInt(anchorData.subCategory, 10) === anchorID);
      const userIsBanned = subscribedToAnchor?.[0]?.isBanned;
      setIsBanned(userIsBanned);
    }
  }, [anchorId, user?.user?.account]);

  useEffect(() => {
    const currentAnchor = anchors?.find((anchor) => anchor.id === anchorId);
    setBlurringForContent(currentAnchor);
    setBanStatus(currentAnchor);
    setAnchorShortUrl(buildAnchorLink({
      id: anchorId,
      name: currentAnchor?.name,
    }));
  }, [anchorId, anchors, setBanStatus, setBlurringForContent]);

  const checkIfUserCanAccessContent = useCallback(() => {
    const isClipStoreTab = payPerViewNav === 'New' && typeOfListing === 'clipstore';
    const { authenticated } = user || {};
    if (!isBanned && ((isClipStorePage || isClipStoreTab) && authenticated)) {
      return true;
    }

    return false;
  }, [isBanned, isClipStorePage, payPerViewNav, typeOfListing, user]);

  useEffect(() => {
    setUserCanAccessContent(checkIfUserCanAccessContent());
  }, [checkIfUserCanAccessContent]);

  const formatUrl = useCallback((url, galleryID) =>
    url.replace('{id}', encodeURIComponent(anchorId)).
      replace('{galleryId}', encodeURIComponent(galleryID)), [anchorId]);

  const getUrlForClip = useCallback(() => {
    if (typeOfListing !== 'clipstore') {
      return ANCHOR_PRIVATE_GALLERY;
    }

    return ANCHOR_VIEW_PURCHASED_CLIPSTORE_GALLERY;
  }, [typeOfListing]);

  const getGalleryID = useCallback(() => {
    if (!PayPerViewStore.newItemWasPurchased) {
      return media?.id;
    }

    return PayPerViewStore.purchasedClip.id;
  }, [media?.id]);

  const fetchClip = useCallback((url) => {
    callApi(url, 'GET', undefined, undefined, undefined, true, true, true).
      then((res) => {
        setPhotos(res.data);
        PayPerViewStore.setNewItemWasPurchased(false);
      }).
      catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const { selectedClipPrice } = props || {};
    const url = formatUrl(getUrlForClip(), getGalleryID());
    if (isOpen) {
      fetchClip(url);
    }
    if (PayPerViewStore.newItemWasPurchased &&
      selectedClipPrice.id === media.id) {
      fetchClip(url);
      setIsOpen(true);
    }

  }, [
    media?.id,
    isOpen,
    typeOfListing,
    anchorId,
    getUrlForClip,
    getGalleryID,
    formatUrl,
    fetchClip,
    props,
  ]);

  const imagePreviewContentRef = useRef();
  const sliderRef = useRef();

  useEffect(() => {
    if (isOpen) {
      imagePreviewContentRef.current.focus();
      if (isMobileSafari) {
        setCurrentBodyScrollTop(window.scrollY);
        document.body.style.position = 'fixed';
      } else {
        document.body.style.overflow = 'hidden';
      }
    } else if (isMobileSafari) {
      document.body.style.position = 'static';
      if (currentBodyScrollTop) {
        window.scroll(0, currentBodyScrollTop);
      }
    } else {
      document.body.style.overflow = 'visible';
    }
  }, [currentBodyScrollTop, isOpen]);


  const imagePreviewContentClickHandler = useCallback((event) => {
    const { target } = event.nativeEvent;
    if (target.tagName === 'DIV') {
      setIsOpen(false);
    }
  }, []);

  const getGalleryItemLink = useCallback(() => {
    const { isPurchasedTab, isClipStorePage } = props;
    const canViewContent = userCanAccessContent || subscribed;
    if ((isPurchasedTab || (typeOfListing === 'clipstore' && subscribed)) && !isClipStorePage) {
      return '?filter=clipstore';
    } else if (canViewContent) {
      return '#';
    }

    return linkToPaymentPage(subscribed, anchorId, anchorShortUrl);
  }, [props, userCanAccessContent, subscribed, typeOfListing, anchorId, anchorShortUrl]);

  const sentToAcordingLink = useCallback(() => {
    if (userCanAccessContent || subscribed) {
      return `/${anchorShortUrl}`;
    }

    return buildAnchorCheckoutLink(anchorId, props.anchorShortUrl);
  }, [anchorId, anchorShortUrl, props.anchorShortUrl, subscribed, userCanAccessContent]);

  const imagePreviewContentKeyDownHandler = useCallback((event) => {
    switch (event.keyCode) {
      case 27:
        setIsOpen(false);
        break;
      case 39:
        sliderRef.current.slickNext();
        break;
      case 37:
        sliderRef.current.slickPrev();
        break;
      default:
        break;
    }
  }, []);

  const openClipStorePaymentModal = useCallback(() => {
    props.openPayPerViewModal(price, title, media.id, media.type, customPriceForFanPlus);
    PayPerViewStore.setCurrentlySelectedAnchor(media.anchorName);
    PayPerViewStore.setCurrentlySelectedAnchorID(media.anchorId);
  }, [
    customPriceForFanPlus,
    media?.anchorId,
    media?.anchorName,
    media?.id,
    media?.type,
    price,
    props,
    title,
  ]);

  const handleContentClick = useCallback(() => {
    const isClipStoreTab = payPerViewNav === 'New' && typeOfListing === 'clipstore';
    const shouldShowClipStorePaymentModal = !isBanned && isClipStoreTab;

    if (isBanned) {
      NotificationStore.error('Banned from following this model. Please contact support.');
    } else if (userCanAccessContent && shouldShowClipStorePaymentModal) {
      openClipStorePaymentModal();
    } else {
      setIsOpen((prevValue) => !prevValue);
    }
  }, [isBanned, openClipStorePaymentModal, payPerViewNav, typeOfListing, userCanAccessContent]);

  const leftTitle = `Gallery, ${imageCount} ${imageCount === 1 ? 'picture' : 'pictures'}`;

  if (liveEvents) {
    return (
      <div className="content-item-container">
        <LiveEventsContent
          coverPhoto={coverPhoto}
          isGallery={true}
          isLiveEventsSection={liveEvents}
          linkToDirectTo={sentToAcordingLink()}
          shouldBlurContent={shouldBlurContent}
        />
      </div>
    );
  }

  return (
    <div className="content-item-container">
      <React.Fragment>
        <Link
          className="content-item gallery-item"
          to={getGalleryItemLink()}
          onClick={handleContentClick}
        >

          <div
            className="content-item-thumbnail private-gallery-thumbnail"
          >
            <div
              className={classnames({
                'private-gallery-content-preview': true,
                'blur': shouldBlurContent,
              })}
              style={{
                background: `url(${image}) 
                            no-repeat top center / 
                            ${image ? 'cover' : 'contain'}`,
                backgroundColor: `${media.mainImage ? '' : '#a6a6a6'}`,
              }}
            />
            <div className="private-gallery-content-after-overlay" />
            <div className="private-gallery-collection-icon">
              <div
                className={classnames('private-video-details',
                  { 'full-height': (subscribed && isFanzone) || isPurchasedTab })
                }
              >
                <CollectionsIcon style={{ fontSize: '80px' }} />
                <SubscribeToViewCTAButton showButton={!subscribed && isFanzone} />
                <PriceForClipStoreItem
                  payPerViewNav={payPerViewNav}
                  price={price}
                />
              </div>
            </div>
            <LockIconContainer
              isLocked={liveEvents}
            />
            {!shouldBlurContent &&
            <div className="private-gallery-count-icon">
              <div className="private-gallery-lock-container">
                <CollectionsIcon
                  style={{ fontSize: '10px' }}
                />
                <div>
                  {imageCount > 9 ? '9+' : imageCount}
                </div>
              </div>
            </div>
            }
          </div>
          <PrivateContentDescription
            allAnchors={allAnchors}
            leftTitle={leftTitle}
            media={media}
            shouldShowDescription={showDescription}
            title={title}
          />
        </Link>
        <LikesAndViewsCount
          anchorId={anchorId}
          isClipStoreContent={isClipStoreContent}
          media={media}
          subscribed={subscribed}
        />
      </React.Fragment>
      <div
        className="image-preview-container"
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        <div className="close-icon" onClick={() => setIsOpen(false)}>
          <Close fontSize="inherit" />
        </div>
        <div
          ref={imagePreviewContentRef}
          className="image-preview-content"
          tabIndex={0}
          onClick={imagePreviewContentClickHandler}
          onKeyDown={imagePreviewContentKeyDownHandler}
        >
          {photos.images &&
            <Slider
              ref={sliderRef}
              autoplay={false}
              dots={false}
              nextArrow={<KeyboardArrowRight fontSize="inherit" />}
              prevArrow={<KeyboardArrowLeft fontSize="inherit" />}
            >
              {photos.images.map((image) => (
                <div key={image.url}>
                  <img alt="" src={image.url} />
                </div>
              ))}
            </Slider>
          }
        </div>
      </div>
    </div>
  );
};

export default PrivateGallery;
